import fs from "node:fs";
import os from "node:os";
import path from "node:path";
import { fileURLToPath } from "node:url";
import { factoryAssetRoots } from "@skaile/workspaces/core";

/**
 * The content-root policy lives in `@skaile/workspaces/core` so the flow
 * connector's resolution surfaces can reach it too — `factory-assets → cli`
 * would be a module cycle. Re-exported here because this module is the CLI's
 * documented path surface and every CLI caller imports it from here.
 *
 * @docLink cli/dev-guide#paths
 */
export { aiResourceRoots } from "@skaile/workspaces/core";

const __ownDir = path.dirname(fileURLToPath(import.meta.url));

/**
 * Walk up from `from` looking for the monorepo marker (`biome.json`, the
 * repo-root shared tooling config). Returns the matching directory or `null`
 * when none is found within eight levels — the latter is the typical case when
 * `@skaile/workspaces` has been installed via `npm i` without a skaile-dev
 * workspace context. Used only to gate dev-only commands (e.g.
 * `serve --compile-test`); first-party content is located via
 * {@link aiResourceRoots}, not this.
 */
function findMonorepoRoot(from: string): string | null {
  let dir = from;
  for (let i = 0; i < 8; i++) {
    if (fs.existsSync(path.join(dir, "biome.json"))) return dir;
    const parent = path.dirname(dir);
    if (parent === dir) break;
    dir = parent;
  }
  return null;
}

/**
 * Absolute path to the monorepo root, or `null` when the CLI is running
 * outside the skaile-dev workspace (typical for `npm i -g` installs).
 *
 * Consumers that only make sense inside the workspace (e.g.
 * `skaile serve --compile-test`) MUST check for `null` and report a
 * "monorepo-only feature" error before proceeding.
 *
 * @docLink cli/dev-guide#paths
 */
export const MONOREPO_ROOT: string | null = findMonorepoRoot(__ownDir);

/**
 * Default content root used by path display and the `validate` command.
 *
 * The primary first-party `factory-assets/` root resolved through the shared
 * `factoryAssetRoots()` helper — the `SKAILE_FACTORY_ASSETS_DIR` override when
 * set, else the in-repo source during dev / bundled `dist/factory-assets/` after
 * `npm i`. Falls back to `~/.skaile/libraries/` — the user-library directory
 * managed by `skaile library add` — when the package cannot be located.
 * Consumers `fs.existsSync` first and degrade to "no skills / flows discovered".
 *
 * @docLink cli/dev-guide#paths
 */
export const AI_RESOURCES: string =
  factoryAssetRoots()[0] ?? path.join(os.homedir(), ".skaile", "libraries");
